import * as Yup from 'yup';
import 'yup-phone-lite';
import { formatLocalDateToUtcTime, parseUtcTimeToLocalDate } from './date-time.helper';
import { normalizeText } from 'components/Rental/RentalForm';

// Validates the phone number against the sibling `country` / `code` on the same
// phone object, matching the convention used across the app (RentalForm,
// InviteGuestForm, inviteGuestValidation, ...): `${code}${number}`, no `+` prefix.
export const isValidPhoneForCountry = (code, number, country) => {
    if (!number) return false;
    try {
        return Yup.string().phone(country).isValidSync(`${code}${number}`);
    } catch {
        return false;
    }
};

const rentalPhoneNumberSchema = () =>
    Yup.string()
        .required('Phone is required')
        .test('is-valid-phone', 'Phone number needs to be valid', function (value) {
            const { country, code } = this.parent;
            return isValidPhoneForCountry(code, value, country);
        });

const rentalPersonPhoneShape = () =>
    Yup.object().shape({
        number: rentalPhoneNumberSchema(),
        code: Yup.mixed(),
        country: Yup.string()
    });

export const getFilteredRenterOptions = ({
    renterOptions = [],
    searchValue = '',
    isGuest = false
}) => {
    const normalizedSearch = String(searchValue).trim().toLowerCase();

    if (!normalizedSearch) {
        return [];
    }

    const normalizedPhoneSearch = normalizedSearch.replace(/\D/g, '');

    return renterOptions
        .map(renter => {
            const firstName = String(renter.first_name || '').toLowerCase();
            const lastName = String(renter.last_name || '').toLowerCase();

            const fullName = [firstName, lastName].filter(Boolean).join(' ').trim();

            const email = String(renter.email || '').toLowerCase();

            const phone = String(renter.phone?.number || '').replace(/\D/g, '');

            const matchesName =
                fullName.includes(normalizedSearch) ||
                firstName.includes(normalizedSearch) ||
                lastName.includes(normalizedSearch);

            const matchesEmail = email.includes(normalizedSearch);

            const matchesPhone =
                normalizedPhoneSearch && phone.includes(normalizedPhoneSearch);

            if (!matchesName && !matchesEmail && !matchesPhone) {
                return null;
            }

            let score = 0;

            // strongest matches
            if (fullName === normalizedSearch) score += 120;
            if (email === normalizedSearch) score += 110;
            if (phone === normalizedPhoneSearch) score += 110;

            // startsWith priority
            if (fullName.startsWith(normalizedSearch)) score += 90;
            if (firstName.startsWith(normalizedSearch)) score += 80;
            if (lastName.startsWith(normalizedSearch)) score += 70;
            if (email.startsWith(normalizedSearch)) score += 60;

            // contains fallback
            if (matchesName) score += 40;
            if (matchesEmail) score += 30;
            if (matchesPhone) score += 30;

            return {
                ...renter,
                matchScore: score
            };
        })
        .filter(Boolean)
        .sort((a, b) => b.matchScore - a.matchScore)
        .slice(0, 5);
};
export const emptyRentalPerson = () => ({
    phone: {
        number: '',
        code: '1',
        country: 'US'
    },
    first_name: '',
    last_name: '',
    email: '',
    minor: false,
    renterFound: undefined
});

export const getDefaultTime = (hours, minutes = 0) => {
    const date = new Date();
    date.setHours(hours, minutes, 0, 0);
    return date;
};

export const getScopeLabel = scope =>
    scope?.userProperty?.address?.full_address ||
    scope?.userProperty?.full_address ||
    scope?.userProperty?.address_1 ||
    scope?.propertyMetadata?.full_address ||
    scope?.propertyMetadata?.address_1 ||
    scope?.propertyId ||
    '';

export const getSelectedScope = (propertyScopes, propertyId) =>
    propertyScopes.find(scope => String(scope.propertyId) === String(propertyId));

export const getFormattedDateTime = (date, time) => {
    const dt = new Date(date);

    const utcTime = formatLocalDateToUtcTime(time);
    const [hours, minutes] = utcTime.split(':').map(Number);

    dt.setHours(hours, minutes, 0, 0);

    const year = dt.getFullYear();
    const month = String(dt.getMonth() + 1).padStart(2, '0');
    const day = String(dt.getDate()).padStart(2, '0');
    const h = String(dt.getHours()).padStart(2, '0');
    const m = String(dt.getMinutes()).padStart(2, '0');

    return `${year}-${month}-${day} ${h}:${m}`;
};

export const rentalFormValidationSchema = Yup.object().shape({
    property_id: Yup.string().required('Property is required'),
    check_in_date: Yup.mixed().required('Check-in date is required'),
    check_out_date: Yup.mixed().required('Check-out date is required'),
    check_in_time: Yup.mixed().required('Check-in time is required'),
    check_out_time: Yup.mixed().required('Check-out time is required'),
    renter: Yup.object().shape({
        phone: rentalPersonPhoneShape(),
        first_name: Yup.string().required('First name required'),
        last_name: Yup.string().required('Last name required'),
        email: Yup.string().email('Invalid email').optional()
    }),
    guests: Yup.array().of(
        Yup.object().shape({
            phone: rentalPersonPhoneShape(),
            first_name: Yup.string().required('First name required'),
            last_name: Yup.string().required('Last name required'),
            email: Yup.string().email('Invalid email').nullable().optional()
        })
    )
});

export const buildRentalInitialValues = ({ selectedRental, defaultScope }) => {
    if (!selectedRental) {
        return {
            property_id: defaultScope?.propertyId || '',
            check_in_date: null,
            check_in_time:
                parseUtcTimeToLocalDate(defaultScope?.org?.check_in_time) ||
                getDefaultTime(12, 0),
            check_out_date: null,
            check_out_time:
                parseUtcTimeToLocalDate(defaultScope?.org?.check_out_time) ||
                getDefaultTime(10, 0),
            renter: emptyRentalPerson(),
            guests: []
        };
    }

    return {
        property_id: selectedRental.property_id,
        check_in_date: new Date(selectedRental.start_date._seconds * 1000),
        check_in_time: new Date(selectedRental.start_date._seconds * 1000),
        check_out_date: new Date(selectedRental.end_date._seconds * 1000),
        check_out_time: new Date(selectedRental.end_date._seconds * 1000),
        renter: {
            phone: {
                number:
                    selectedRental.renter?.phone?.number ||
                    selectedRental.renter_user?.phone?.number ||
                    selectedRental.renter_invite?.phone?.number ||
                    '',
                code: String(
                    selectedRental.renter?.phone?.code ||
                        selectedRental.renter_user?.phone?.code ||
                        selectedRental.renter_invite?.phone?.code ||
                        '1'
                ),
                country:
                    selectedRental.renter?.phone?.country ||
                    selectedRental.renter_user?.phone?.country ||
                    selectedRental.renter_invite?.phone?.country ||
                    'US'
            },
            first_name:
                selectedRental.renter?.first_name ||
                selectedRental.renter_user?.first_name ||
                selectedRental.renter_invite?.first_name ||
                '',
            last_name:
                selectedRental.renter?.last_name ||
                selectedRental.renter_user?.last_name ||
                selectedRental.renter_invite?.last_name ||
                '',
            email:
                selectedRental.renter?.email ||
                selectedRental.renter_user?.email ||
                selectedRental.renter_invite?.email ||
                '',
            renterFound: Boolean(
                selectedRental.renter?.id ||
                    selectedRental.renter_user?.id ||
                    selectedRental.renter_invite?.id
            )
        },
        guests:
            selectedRental.guests?.map(guest => ({
                id: guest.id,
                phone: {
                    number: guest.phone?.number || '',
                    code: String(guest.phone?.code || '1'),
                    country: guest.phone?.country || 'US'
                },
                first_name: guest.first_name || '',
                last_name: guest.last_name || '',
                email: guest.email || '',
                minor: guest.minor || false,
                renterFound: guest.source === 'user' || guest.source === 'invite'
            })) || []
    };
};

export const buildCreateRentalPayload = ({ values, orgId, propertyId }) => ({
    org_id: orgId,
    property_id: propertyId,
    start_date: getFormattedDateTime(values.check_in_date, values.check_in_time),
    end_date: getFormattedDateTime(values.check_out_date, values.check_out_time),
    renter: {
        phone: {
            number: values.renter.phone.number,
            code: Number(values.renter.phone.code),
            country: values.renter.phone.country
        },
        first_name: normalizeText(values.renter.first_name),
        last_name: normalizeText(values.renter.last_name),
        email: normalizeText(values.renter.email)
    },
    guests: values.guests.map(guest => ({
        phone: {
            number: guest.phone.number,
            code: Number(guest.phone.code),
            country: guest.phone.country
        },
        first_name: normalizeText(guest.first_name),
        last_name: normalizeText(guest.last_name),
        email: normalizeText(guest.email),
        minor: guest.minor
    }))
});

export const buildUpdateRentalPayload = ({ values, removedGuests }) => ({
    start_date: getFormattedDateTime(values.check_in_date, values.check_in_time),
    end_date: getFormattedDateTime(values.check_out_date, values.check_out_time),
    added_guests: values.guests
        .filter(guest => !guest.id)
        .map(guest => ({
            phone: {
                number: guest.phone.number,
                code: Number(guest.phone.code),
                country: guest.phone.country
            },
            first_name: normalizeText(guest.first_name),
            last_name: normalizeText(guest.last_name),
            email: normalizeText(guest.email),
            minor: guest.minor
        })),
    removed_guest_ids: removedGuests
});
